import { useState } from 'react'
import type { FormEvent } from 'react'
import { EMAIL_PATTERN, GENERIC_ERROR_MESSAGE, NETWORK_ERROR_MESSAGE } from '../../config/forms'
import { PublicFormRouteEnum, publicFormUrl } from '../../config/publicApi'

// Standalone Google Play "delete account" page — no marketing header/footer
// (mirrors NotFound's placement outside Layout, see App.tsx). Content and
// email-only form mirror ContactSection.tsx's fetch/validation pattern.
export const DeleteAccount = () => {
  const [email, setEmail] = useState('')
  const [invalid, setInvalid] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const updateEmail = (e: FormEvent<HTMLInputElement>) => {
    setEmail(e.currentTarget.value)
    setInvalid(false)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setServerError(null)

    const trimmedEmail = email.trim()
    if (!trimmedEmail || !EMAIL_PATTERN.test(trimmedEmail)) {
      setInvalid(true)
      return
    }

    const url = publicFormUrl(PublicFormRouteEnum.DeleteAccount)
    if (!url) {
      setServerError(GENERIC_ERROR_MESSAGE)
      return
    }

    try {
      setSubmitting(true)
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail }),
      })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result?.success) {
        setServerError(result?.error ?? GENERIC_ERROR_MESSAGE)
        return
      }

      setSubmitted(true)
      setEmail('')
    } catch {
      setServerError(NETWORK_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="wrap delete-account">
      <h1>Delete your QuickScore account</h1>
      <p className="eyebrow">QuickScore · Rommel C. Manalo</p>

      <section>
        <h2>How to request deletion</h2>
        <ol>
          <li>Enter the email you signed up with below.</li>
          <li>Submit the form.</li>
          <li>We delete your account within 30 days and email you to confirm.</li>
        </ol>
      </section>

      <section>
        <h2>What gets deleted / what we keep</h2>
        <p>
          <strong>Deleted:</strong> account, scanned answer sheets, scores &amp; results, student data you entered.
        </p>
        <p>
          <strong>Kept:</strong> only records we are legally required to retain, for the required retention period.
        </p>
      </section>

      <form className="lead" onSubmit={handleSubmit} noValidate>
        {submitted ? <div className="form-ok show">Request received — we&apos;ll email you. ✓</div> : null}

        {serverError ? (
          <div className="form-err show" role="alert">
            {serverError}
          </div>
        ) : null}

        <div className={`field${invalid ? ' invalid' : ''}`}>
          <label htmlFor="f-email">Email you signed up with</label>
          <input
            id="f-email"
            name="email"
            type="email"
            placeholder="you@school.edu"
            required
            value={email}
            onChange={updateEmail}
            disabled={submitting}
          />
          <div className="err">Please enter a valid email address.</div>
        </div>

        <button className="btn btn-primary" type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Request account deletion'}
        </button>
      </form>
    </main>
  )
}

export default DeleteAccount
